import { forwardRef } from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'

import Icon from 'components/Shared/Icon/Index'
import colors from 'components/utils/colors'

const styleAsSecondary = css`
  color: ${colors.secondary40};
  border: 2px solid ${colors.secondary70};
  min-width: 88px;

  ${props => !props.disabled && styleAsEnabledSecondary}
`

const styleAsEnabledSecondary = css`
  &&:hover {
    background: ${colors.secondary60};
  }
  &&:focus-visible {
    background: ${colors.secondary80};
  }
  &&:active, &&.active {
    background: ${colors.secondary40};
  }
`

const styleAsEnabled = css`
  cursor: pointer;
  &:hover, &:focus-visible, &:active, &.active {
    color: #FFFFFF;
    border: 2px solid transparent;
  }

  &.active {
    background: ${colors.primary35};
  }

  &:hover {
    background: ${colors.primary40};
  }

  &:focus-visible {
    background: ${colors.primary60};
    outline: none;
  }

  &:active {
    background: ${colors.primary35};
  }
`

const styleAsDisabled = css`
  color: ${colors.secondary80};
  border: 2px solid ${colors.secondary90};

  &.active {
    background: ${colors.secondary90};
    color: #FFFFFF;
  }
`

const styleAsLarge = css`
  font-size: 16px;
  height: 48px;
  padding: 0 ${props => props.$secondary ? '32' : '48'}px;
`

const styleAsAlert = css`
  color: ${colors.secondary40};
  border: 2px solid ${colors.alert};

  ${props => !props.disabled && styleAsEnabledAlert}
`

const styleAsEnabledAlert = css`
  &&.active {
    background: ${colors.alert65};
  }
  &&:hover {
    background: ${colors.alert};
  }
  &&:focus-visible {
    background: ${colors.alert75};
  }
  &&:active {
    background: ${colors.alert65};
  }
`

const styleAsSuccess = css`
  color: ${colors.secondary40};
  border: 2px solid ${colors.success53};

  ${props => !props.disabled && styleAsEnabledSuccess}
`

const styleAsEnabledSuccess = css`
  &&.active {
    background: ${colors.success45};
  }
  &&:hover {
    background: ${colors.success53};
  }
  &&:focus-visible {
    background: ${colors.success63};
  }
  &&:active {
    background: ${colors.success45};
  }
`

const styleAsPaused = css`
  color: ${colors.secondary40};
  border: 2px solid ${colors.paused55};

  ${props => !props.disabled && styleAsEnabledPaused}
`

const styleAsEnabledPaused = css`
  &&.active {
    background: ${colors.paused47};
  }
  &&:hover {
    background: ${colors.paused55};
  }
  &&:focus-visible {
    background: ${colors.paused65};
  }
  &&:active {
    background: ${colors.paused47};
  }
`

const styleAsWithIcon = css`
  padding: 0 16px;
  min-width: initial;
`

const StyledIcon = styled(Icon)`
  color: inherit;

  &:before {
    color: inherit;
  }
`

export const StyledButton = styled.button`
  width: ${props => props.width ? props.width : 'auto'};
  min-width: 112px;
  height: 32px;
  font-family: Exo;
  font-weight: 500;
  font-size: 14px;
  border-radius: 3px;
  background-color: #FFFFFF;
  color: ${colors.primary35};
  border: 2px solid ${colors.primary40};
  padding: 0 ${props => props.$secondary ? '16' : '24'}px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  user-select: none;

  ${props => props.$secondary && styleAsSecondary}
  ${props => props.$large && styleAsLarge}
  ${props => props.alert && styleAsAlert}
  ${props => props.success && styleAsSuccess}
  ${props => props.paused && styleAsPaused}
  ${props => props.disabled ? styleAsDisabled : styleAsEnabled}
  ${props => props.$withIcon && styleAsWithIcon}
`

const Button = forwardRef(({ secondary, disabled, children, width, large, type, active, iconName, ...rest }, ref) => {
  return (
    <StyledButton
      className={active ? 'active' : ''}
      $secondary={secondary}
      disabled={disabled}
      $large={large}
      width={width}
      type={type || 'button'}
      ref={ref}
      $withIcon={!!iconName}
      {...rest}
    >
      {iconName && <StyledIcon iconName={iconName} />}
      {children}
    </StyledButton>
  )
})

Button.displayName = 'Button'

Button.propTypes = {
  secondary: PropTypes.bool,
  disabled: PropTypes.bool,
  width: PropTypes.string,
  large: PropTypes.bool,
  children: PropTypes.node,
  type: PropTypes.string,
  active: PropTypes.bool,
  iconName: PropTypes.string,
  onClick: PropTypes.func,
}

export default Button
